import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './PrivateViewing.css';
import paintingsData from '../data/paintings.json';

const PrivateViewing = () => {
    const [loading, setLoading] = useState(true);

    // State to track which painting stories are expanded
    const [expandedStories, setExpandedStories] = useState({});

    // Scroll to top on load and trigger loader
    useEffect(() => {
        window.scrollTo(0, 0);

        setLoading(true);
        document.body.style.overflow = "hidden";

        const timer = setTimeout(() => {
            setLoading(false);
            document.body.style.overflow = "auto";
        }, 900);

        return () => {
            clearTimeout(timer);
            document.body.style.overflow = "auto";
        };
    }, []);

    const fadeUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.25, 0.46, 0.45, 0.94] } }
    };

    const scrollToNext = () => {
        document.querySelector('.pv-dossier-concept').scrollIntoView({ behavior: 'smooth' });
    };

    // Function to toggle read more/less for a specific item
    const toggleStory = (id) => {
        setExpandedStories(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    // Only show works that are currently available in this private-viewing collection.
    const visiblePaintings = paintingsData.filter((item) => item.sold !== true);

    return (
        <div className="pv-exhibition-wrapper">
            <div className="pv-dossier-container">

                {/* --- AESTHETIC BACKGROUND LOADER --- */}
                <AnimatePresence>
                    {loading && (
                        <motion.div
                            key="elite-loader"
                            initial={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.8, ease: "easeInOut" }}
                            style={{
                                position: "fixed", inset: 0, zIndex: 99999,
                                backgroundColor: "#fcfbf9", display: "flex",
                                flexDirection: "column", justifyContent: "center", alignItems: "center"
                            }}
                        >
                            <motion.span
                                animate={{ opacity: [0.3, 1, 0.3] }}
                                transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                                style={{
                                    fontFamily: "'Playfair Display', serif", fontSize: "1rem",
                                    textTransform: "uppercase", letterSpacing: "6px", color: "#111111", marginBottom: "20px"
                                }}
                            >
                                Curating Exhibition
                            </motion.span>
                            <div style={{ width: "80px", height: "1px", backgroundColor: "rgba(0,0,0,0.05)", overflow: "hidden", position: "relative" }}>
                                <motion.div
                                    initial={{ x: "-100%" }} animate={{ x: "100%" }}
                                    transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
                                    style={{ width: "100%", height: "100%", backgroundColor: "#c4a36e", position: "absolute" }}
                                />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* NAVIGATION */}
                <nav className="pv-dossier-nav">
                    <div className="pv-nav-brand">VIVEK KUMAR VERMA</div>
                    <div className="pv-nav-status">Private Viewing</div>
                </nav>

                {/* SECTION 1: THE HERO */}
                <section className="pv-dossier-hero">
                    <motion.div
                        className="pv-hero-video-wrapper"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.5 }}
                        style={{ backgroundImage: "url('https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597523/main-bg.png')" }}
                    >
                        <div className="pv-video-overlay"></div>
                    </motion.div>

                    <div className="pv-hero-text-content">
                        <motion.span className="pv-hero-subtitle" initial="hidden" animate="visible" variants={fadeUp}>
                            Jehangir Art Gallery · Exclusive Preview
                        </motion.span>
                        <motion.h1 className="pv-hero-title" initial="hidden" animate="visible" variants={fadeUp}>
                            Anjaan's World Frames - II
                        </motion.h1>
                        <motion.p className="pv-hero-descriptor" initial="hidden" animate="visible" variants={fadeUp}>
                            Pictorial Biography · Karma Pradhan · Contemporary Narratives
                        </motion.p>
                    </div>

                    <motion.div
                        className="pv-scroll-indicator"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2, duration: 1 }}
                        onClick={scrollToNext}
                    >
                        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 5v14M19 12l-7 7-7-7" />
                        </svg>
                    </motion.div>
                </section>

                {/* SECTION 2: THE CONCEPT */}

                <section className="pv-dossier-concept">
                    <motion.div
                        className="pv-concept-grid"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                        variants={fadeUp}
                    >
                        <div className="pv-concept-text">
                            <span className="pv-section-label">The Core Concept</span>

                            <h2>Pictorial Biography In A Single Frame</h2>

                            <p>
                                Anjaan Vivek portrays a life through actions, choices, struggles
                                and achievements rather than physical likeness alone.
                            </p>

                            <p>
                             Rooted in <strong>Karma Pradhan</strong>, the work explores a person through the choices they make, the struggles they endure, and the impact they leave behind. Identity emerges not from appearance, but from the inner forces, emotions, actions and experiences that shape a life
                            </p>

                            <p>
                                <strong>The World Frames</strong> is an evolving series portraying
                                the modern titans shaping our time — a continuing visual archive
                                of lives, actions and ideas influencing the future.
                            </p>
                        </div>

                        <div className="pv-concept-image">
                            <img
                                src="https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597518/elon-frame.jpg"
                                alt="The World Frames – Pictorial Biography Artwork Detail"
                            />
                        </div>
                    </motion.div>
                </section>




                {/* SECTION 3: FEATURED MASTERWORKS */}
                <section className="pv-dossier-gallery">
                    <motion.div className="pv-gallery-header" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                        <span className="pv-section-label">Exhibition Showcase</span>
                        <h2>Works in the Exhibition</h2>
                    </motion.div>

                    <div className="pv-gallery-list">
                        {visiblePaintings.map((item, index) => {
                            const baseImage = item.images?.find((image) => image.imageType === "BASE")?.imageUrl || item.images?.[0]?.imageUrl || null;
                            const hoverImage = item.images?.find((image) => image.imageUrl !== baseImage)?.imageUrl || null;
                            const isExpanded = expandedStories[item.id];
                            const storyContent = item.story || item.seriesDescription || "A profound masterwork by Anjaan Vivek.";
                            return (

                                <motion.div
                                    key={item.id}
                                    className={`pv-gallery-item ${index % 2 !== 0 ? 'pv-reverse' : ''}`}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true, amount: 0.2 }}
                                    variants={fadeUp}
                                >
                                    {baseImage && (
                                        <div className="pv-item-image">
                                            <img src={baseImage} alt={item.title} className="pv-base-img" />
                                            {hoverImage && (
                                                <img src={hoverImage} alt={`${item.title} Alternate`} className="pv-hover-img" />
                                            )}
                                        </div>
                                    )}
                                    <div className="pv-item-details">
                                        <h3>{item.title}</h3>
                                        <div className="pv-item-specs">
                                            <span>{item.material}</span>
                                            <span className="pv-spec-dot">•</span>
                                            <span>{item.size}</span>
                                            <span className="pv-spec-dot">•</span>
                                            <span>{item.year}</span>
                                        </div>

                                        {/* HTML Content Rendering */}
                                        <div className="pv-story-container">
                                            <div
                                                className={`pv-html-content ${isExpanded ? 'pv-expanded' : 'pv-collapsed'}`}
                                                dangerouslySetInnerHTML={{ __html: storyContent }}
                                            />
                                            <button
                                                className="pv-read-more-btn"
                                                onClick={() => toggleStory(item.id)}
                                            >
                                                {isExpanded ? '— Close Artist’s Note' : '+ Read Artist’s Note'}
                                            </button>
                                        </div>

                                        <a
                                            href={`https://wa.me/919300847841?text=${encodeURIComponent(`Dear Studio,\n\nI would like to request further details regarding '${item.title}'.`)}`}
                                            className="pv-btn-inquire"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Private Enquiry
                                        </a>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </section>

                {/* SECTION 4: THE ARTIST DOSSIER */}
                <section className="pv-dossier-artist">
                    <motion.div className="pv-artist-grid" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={fadeUp}>
                        <div className="pv-artist-photo">
                            <img src="https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597515/artist.jpg" alt="Vivek Kumar Verma Anjaan" />
                        </div>
                        <div className="pv-artist-bio">
                            <span className="pv-section-label">The Artist</span>
                            <h2>Vivek Kumar Verma 'Anjaan'</h2>
                            <p>
                                Anjaan Vivek’s practice explores narrative, identity and human experience through figurative and semi-abstract compositions shaped by decades of sustained artistic practice.
                            </p>
                            <p>
                                Central to his current work is <strong>Pictorial Biography in a Single Frame</strong> — an approach rooted in <strong>Karma Pradhan</strong>, where a person is revealed through actions, choices and impact rather than likeness alone.
                            </p>
                        </div>
                    </motion.div>
                </section>

                {/* FOOTER: PRIVATE CONTACT */}
                <footer className="pv-dossier-footer">

                    <div className="pv-footer-bottom">
                        <span>© 2026 Vivek Kumar Verma 'Anjaan'</span>
                        <span>All Rights Reserved</span>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default PrivateViewing;